import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useDashboard } from "../context/DashboardContext";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function Dashboard() {
  const { counts, monthlyUsers, monthlyrevenue, notifications, loading } = useDashboard();

  const stats = [
    {
      label: "Total Users",
      value: counts.users_count,
      icon: "👥",
      tone: "blue",
      description: "Active registered users"
    },
    {
      label: "Courses",
      value: counts.courses_count,
      icon: "📚",
      tone: "green",
      description: "Available courses"
    },
    {
      label: "Documents",
      value: counts.documents_count,
      icon: "📄",
      tone: "amber",
      description: "Uploaded documents"
    },
    {
      label: "Revenue",
      value: `₹ ${counts.current_month_revenue}`,
      icon: "💳",
      tone: "red",
      description: "Monthly revenue"
    },
  ];

  const formatTimeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diff = Math.floor((now - past) / 1000);

  if (diff < 60) return `${diff} sec ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

const getIcon = (type) => {
  switch (type) {
    case "user": return "👤";
    case "document": return "📄";
    case "course": return "📚";
    case "subscription": return "💳";
    default: return "🔔";
  }
};

const recentActivities = (notifications || []).map(item => ({
  icon: getIcon(item.type),
  text: item.title,
  time: formatTimeAgo(item.create_audit_time),
  type: item.type
}));

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm p-8 text-center">
          <div className="text-lg font-extrabold text-gray-900">Loading dashboard...</div>
          <p className="mt-2 text-sm text-gray-600">Please wait while we fetch your data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Pill tone="red">
            <span className="mr-1">📊</span>
            Admin Dashboard
          </Pill>
          <Pill tone="gray">Real-time data</Pill>
          <Pill tone="green">All systems operational</Pill>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
          Welcome back, Admin
        </h1>
        <p className="mt-2 text-gray-600">
          Here's what's happening with your platform today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((item, i) => (
          <article
            key={i}
            className="group rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <Pill tone={item.tone} size="sm">{item.label}</Pill>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-3xl font-extrabold text-gray-900">
                  {item.value || "—"}
                </div>
                <p className="text-sm text-gray-600">
                  {item.description}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>

      <SectionDivider
        label="Analytics"
        right={
          <div className="flex items-center gap-2">
            <Pill tone="blue" size="sm">Last 6 months</Pill>
          </div>
        }
      />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* User Growth Chart */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-gray-900">
                  User Growth
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Monthly user registrations
                </p>
              </div>
              <Pill tone="green" size="sm">
                <span className="mr-1">📈</span>
                +23% growth
              </Pill>
            </div>
          </div>

          <div className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyUsers}>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                />
                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar
                  dataKey="users"
                  fill="#DC2626"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Revenue Trend
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Monthly revenue in ₹ thousands
                </p>
              </div>
              <Pill tone="amber" size="sm">
                <span className="mr-1">💰</span>
                ₹ {counts.current_month_revenue} this month
              </Pill>
            </div>
          </div>

          <div className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyrevenue}>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#DC2626"
                  strokeWidth={3}
                  dot={{ r: 6, fill: '#DC2626' }}
                  activeDot={{ r: 8, fill: '#DC2626' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <SectionDivider
        label="Recent Activity"
        right={
          <Pill tone="gray" size="sm">Live updates</Pill>
        }
      />

      {/* Recent Activity */}
      <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-extrabold text-gray-900">
            Latest Platform Activity
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Real-time updates from your platform
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0">
                  <span className="text-xl">{activity.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.text}
                  </p>
                  <p className="text-xs text-gray-500">
                    {activity.time}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Pill tone="gray" size="sm">
                    {activity.type}
                  </Pill>
                </div>
              </div>
            ))}
          </div>

          {/* <div className="mt-6 pt-4 border-t border-gray-100">
            <button className="w-full text-center py-3 px-4 rounded-2xl text-sm font-extrabold text-gray-700 hover:bg-gray-50 transition-colors ring-1 ring-gray-200">
              View all activity
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
}
